import { supabase } from "@/integrations/supabase/client";

export interface StoryMetaDb {
  location?: string;
  date_window?: string;
  address?: string;
}

export interface StorySectionDb {
  heading: string;
  body: string;
  icon?: string;
}

export interface StoryStatusCalloutDb {
  emoji?: string;
  label: string;
  body: string;
}

export interface StoryFaqDb {
  q: string;
  a: string;
}

export interface StoryCrossLinkDb {
  label: string;
  to: string;
}

export interface StorySeoDb {
  title?: string;
  description?: string;
  json_ld_type?: "TouristAttraction" | "Event" | "Article";
}

export interface HappeningStoryRow {
  id: string;
  slug: string;
  title: string;
  badge: string;
  intro: string;
  hero_image_url: string | null;
  hero_alt: string;
  meta: StoryMetaDb;
  status_callout: StoryStatusCalloutDb | null;
  sections: StorySectionDb[];
  tips: string[];
  faq: StoryFaqDb[];
  cross_links: StoryCrossLinkDb[];
  seo: StorySeoDb;
  start_date: string | null;
  end_date: string | null;
  priority: number;
  is_published: boolean;
  is_archived: boolean;
  archived_at: string | null;
  generated_for: string | null;
  generated_by: string | null;
  source_urls: string[];
  source_checked_at: string | null;
  created_at: string;
  updated_at: string;
}

const TABLE = "happening_stories" as const;

export const listHappeningStories = async (): Promise<HappeningStoryRow[]> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .order("is_archived", { ascending: true })
    .order("priority", { ascending: false })
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as HappeningStoryRow[];
};

export const listArchivedStories = async (): Promise<HappeningStoryRow[]> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("is_archived", true)
    .order("archived_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as HappeningStoryRow[];
};

export const getHappeningStoryBySlug = async (
  slug: string,
): Promise<HappeningStoryRow | null> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as HappeningStoryRow | null;
};

export const getCurrentHappeningStory = async (): Promise<HappeningStoryRow | null> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("is_published", true)
    .eq("is_archived", false)
    .order("priority", { ascending: false })
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as HappeningStoryRow | null;
};

export type HappeningStoryUpsert = Omit<
  HappeningStoryRow,
  "id" | "created_at" | "updated_at"
> & { id?: string };

export const upsertHappeningStory = async (row: HappeningStoryUpsert) => {
  const { error } = await supabase.from(TABLE).upsert(row as never, {
    onConflict: "slug",
  });
  if (error) throw error;
};

export const deleteHappeningStory = async (id: string) => {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw error;
};

export const setStoryArchived = async (id: string, archived: boolean) => {
  const { error } = await supabase
    .from(TABLE)
    .update({
      is_archived: archived,
      archived_at: archived ? new Date().toISOString() : null,
    })
    .eq("id", id);
  if (error) throw error;
};

export const setStoryPublished = async (id: string, published: boolean) => {
  // When publishing a story, auto-archive every other currently-live story so
  // only one happening story is featured at a time and the previous one moves
  // into the archive immediately.
  if (published) {
    const { error } = await supabase.rpc("publish_happening_story", { p_story_id: id });
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from(TABLE)
    .update({
      is_published: published,
      // Publishing an archived row should also un-archive it.
      ...(published ? { is_archived: false, archived_at: null } : {}),
    })
    .eq("id", id);
  if (error) throw error;
};

/**
 * Slugs whose source-of-truth is the `happening_stories` table.
 * For these slugs, the bundled TS fallback is NOT shown to the public —
 * if the DB row isn't published, the route 404s.
 */
export const DB_MANAGED_STORY_SLUGS = new Set<string>([
  "orchids-in-the-wild",
  "june-beach-season",
  "october-tidepool-return",
  "november-winter-swell",
]);
